/**
 * 空运询价供应商报价前端接口
 */

import { request } from '@/utils/http'
import { type BatchDeleteRequest } from '@/api/types/common'
import {
  type InitiateConsultationRequest,
  type QuotationBaseModel,
  type QuotationChargingItemVO
} from './quotation-base'

export interface QuotationAirModel extends QuotationBaseModel {
  paymentTerm?: string | null
  loadingCountryId?: string | number | null
  loadingCityId?: string | number | null
  loadingAirportId?: string | number | null
  destinationCountryId?: string | number | null
  destinationCityId?: string | number | null
  destinationAirportId?: string | number | null
  serviceType?: string | null
  hsCode?: string | null
  onePriceSupported?: boolean | null
  quotationDeadline?: string | null
  specialRequirements?: string | null
  onePrice?: boolean | null
  flightNo?: string | null
  flightDepartureTime?: string | null
  flightArrivalTime?: string | null
  routeLevel?: number | null
  chargingItemList?: QuotationChargingItemVO[] | null
}

export const getQuotationAirById = (id: string) => {
  return request.post<QuotationAirModel>('/price/quotation/air/get', { id })
}

export const queryQuotationAirByInquiry = (params: {
  inquiryIds?: Array<string | number>
  inquiryNo?: string | null
}) => {
  return request.post<QuotationAirModel[]>('/price/quotation/air/query', params)
}

export const initiateConsultationAir = (params: InitiateConsultationRequest) => {
  return request.post<boolean>('/price/quotation/air/initiateConsultation', params)
}

export const batchDeleteQuotationAir = (params: BatchDeleteRequest) => {
  return request.post<boolean>('/price/quotation/air/batch-delete', params)
}

import type { LinkAccess, BusinessFileVO } from './quotation-fcl'

/** 空运询价货物（InquiryAirCargoVO） */
export interface InquiryAirCargoVO {
  id?: string
  inquiryId?: string
  /** 航空板型（整板） */
  airPalletType?: number
  /** 板数（整板） */
  palletCount?: number
  /** 集装设备（整舱） */
  containerEquipment?: string
  /** 是否独占货舱（整舱） */
  exclusiveCargoHold?: boolean
  /** 装卸要求（整舱/包机） */
  handlingRequirement?: string
  /** 偏好机型（包机） */
  preferredAircraftType?: number
  /** 件数 */
  quantity?: number
  /** 毛量 */
  weight?: number
  length?: number
  width?: number
  height?: number
  /** 包装方式 */
  packageId?: number
  /** 温度范围-起始 */
  temperatureStart?: number
  /** 温度范围-结束 */
  temperatureEnd?: number
  /** 通风门 0-关 1-开 */
  ventilationDoor?: boolean
  /** 通风量 */
  ventilationQuantity?: number
  /** 湿度 */
  humidity?: number
  /** 是否预冷 */
  preCooling?: boolean
  remarks?: string
  [key: string]: unknown
}

/** 空运报价货物（QuotationAirCargoVO） */
export interface QuotationAirCargoVO extends InquiryAirCargoVO {
  quotationId?: string
}

/** 空运询价基础信息（InquiryAirListVO + 父类关键字段） */
export interface InquiryAirListVO {
  id?: string
  inquiryNo?: string
  status?: number
  /** 付款方式 PREPAID/COLLECT */
  paymentMethod?: string
  /** 服务条款（贸易条款） */
  serviceTerm?: string
  /** 运输条款 */
  transportClause?: string
  /** 询价性质（palletNature 1-询价 2-实单） */
  palletNature?: number
  loadingCountryId?: string
  loadingCityId?: string
  loadingAirportId?: string
  destinationCountryId?: string
  destinationCityId?: string
  destinationAirportId?: string
  /** 服务类型 CONSOL/FULL_PALLET/BLOCK_SPACE/FULL_CHARTER */
  serviceType?: string
  /** 直达/中转 1-直达 2-中转 */
  directTransit?: number
  /** 中转机场（复用 transferPortId1） */
  transferPortId1?: string
  transferPortId2?: string
  transferPortId3?: string
  /** 货好时间 */
  cargoReadyTimeType?: string
  cargoReadyTimeRange?: number
  cargoReadyTimeDate?: string
  /** 期望开航时间（etd） */
  etd?: string
  /** 货物类型 */
  cargoType?: number
  /** UN 编号 */
  unNo?: number
  /** 类别 */
  hazardClass?: number
  /** 品名 */
  cargoDescription?: string
  hsCode?: string
  cargoValue?: number
  cargoValueCurrency?: string
  /** 航空公司 */
  carrierId?: string
  onePriceSupported?: boolean
  quotationDeadline?: string
  specialRequirements?: string
  cargoList?: InquiryAirCargoVO[]
  /** 命中密度等级，如 0 < 1CBM < 166 */
  hitDensityLevel?: string
  /** 命中段，如 p2000 */
  hitSection?: string
  [key: string]: unknown
}

/** 空运报价费用条目（含基础费用档位字段） */
export interface QuotationAirChargingItemVO {
  id?: string
  quotationId?: string
  chargeItemId?: string | number
  /** 费用主类型：1=港前 2=空运 3=尾程 */
  feeMainType?: number
  /** 费用类型：0/1/2/3/4 空运，10/11/12 港前，20/21/22 尾程 */
  feeType?: number
  /** 付费条款：1-预付 2-到付 */
  paymentType?: number
  /** 单位ID */
  measureId?: string
  currency?: string
  /** 单价 */
  unitRate?: number | string
  remarks?: string
  /** 密度等级 */
  densityLevel?: string
  /** M 最低运费 */
  m?: number | string
  /** N 45kg以下普货运价 */
  n?: number | string
  p45?: number | string
  p100?: number | string
  p300?: number | string
  p500?: number | string
  p700?: number | string
  p1000?: number | string
  p2000?: number | string
  /** 潜在费用类型：1-起飞地费用 2-目的地费用 3-空运附加费 */
  potentialFeeType?: number
  /** M 最低运费卖价 */
  msell?: number | string
  /** 单价卖价 */
  unitRateSell?: number | string
  /** 最低收费 */
  minCharge?: number | string
  /** 最高收费 */
  maxCharge?: number | string
  /** 最低收费卖价 */
  minChargeSell?: number | string
  /** 最高收费卖价 */
  maxChargeSell?: number | string
  /** 总价 */
  totalPrice?: number | string
  /** 总价卖价 */
  totalPriceSell?: number | string
  [key: string]: unknown
}

/** 空运报价方案（QuotationAirListVO + 父类字段） */
export interface QuotationAirListVO {
  id?: string
  quotationNo?: string
  status?: number
  inquiryId?: string
  inquiryNo?: string
  supplierId?: string
  supplierName?: string
  contactPhone?: string
  contactEmail?: string
  quoterId?: string
  consultationStatus?: number
  remarks?: string
  supplierRemarks?: string
  onePrice?: boolean
  onePriceSupported?: boolean
  validFrom?: string
  validTo?: string
  /** 货物类型 */
  cargoType?: number
  paymentTerm?: string
  loadingCountryId?: string
  loadingCityId?: string
  loadingAirportId?: string
  destinationCountryId?: string
  destinationCityId?: string
  destinationAirportId?: string
  serviceType?: string
  hsCode?: string
  quotationDeadline?: string
  specialRequirements?: string
  /** 航空公司 */
  carrierId?: string
  /** 航班号 */
  flightNo?: string
  flightDepartureTime?: string
  flightArrivalTime?: string
  /** 航线等级 1-极速 2-标准 3-经济 */
  routeLevel?: number
  /** 班期（周一~周日） */
  weekdays?: number[]
  /** 直达/中转 1-直达 2-中转 */
  directTransit?: number
  /** 中转机场（复用 transferPortId1） */
  transferPortId1?: string
  transferPortInfo?: string[]
  cargoList?: QuotationAirCargoVO[]
  chargingItemList?: QuotationAirChargingItemVO[]
  files?: BusinessFileVO[]
  // 港前字段
  preTransportMode?: number
  prePickupPortId?: string
  preCarrierId?: string
  preVessel?: string
  preVoyage?: string
  preBargeEtd?: string
  preBargeEta?: string
  preTransportationDays?: number
  prePickupRailwayStationId?: string
  preRailCompanyId?: string
  preRailLineType?: string
  preRailTrainNo?: string
  preRailDepartureTime?: string
  preLoadingPortId?: string
  preStuffingTerminalId?: string
  preLoadingRailwayStationId?: string
  preLoadingAirportId?: string
  preAirportTerminal?: string
  preRemarks?: string
  // 尾程字段
  lastTransportMode?: number
  lastDischargingPortId?: string
  lastDeliveryTerminalId?: string
  lastDischargingStationId?: string
  lastDischargingAirportId?: string
  lastAirportTerminal?: string
  lastDestinationPortId?: string
  lastVessel?: string
  lastVoyage?: string
  lastBargeEtd?: string
  lastBargeEta?: string
  lastTransportationDays?: number
  lastDestinationStationId?: string
  lastRailCompanyId?: string
  lastRailLineType?: string
  lastRailTrainNo?: string
  lastRailDepartureTime?: string
  lastRemarks?: string
  [key: string]: unknown
}

/** 空运匿名报价详情（含询价信息） */
export interface QuotationAirAnonymousDetailVO {
  /** 报价单列表 */
  quotations?: QuotationAirListVO[]
  /** 询价单信息 */
  inquiry?: InquiryAirListVO
}

/**
 * 通过 token 查询邮件授权范围内的空运报价方案列表（含询价信息）
 * 后端：QuotationAirController#queryByInquiryAnonymous
 */
export const queryByInquiryAirAnonymous = (access: LinkAccess) =>
  request.post<QuotationAirAnonymousDetailVO>('/price/quotation/air/anonymous/queryByInquiry', { token: access.token })

export const createAnonymousQuotationAirScheme = (quotation: Record<string, unknown>, access: LinkAccess) =>
  request.post<number>('/price/quotation/air/anonymous/createScheme', { quotation, token: access.token })

export const updateAnonymousQuotationAir = (quotation: Record<string, unknown>, access: LinkAccess) =>
  request.post<boolean>('/price/quotation/air/anonymous/update', { quotation, token: access.token })

export const batchSubmitAnonymousQuotationAir = (quotations: Record<string, unknown>[], access: LinkAccess) =>
  request.post<boolean>('/price/quotation/air/anonymous/batchSubmit', { quotations, token: access.token })

export const batchDeleteAnonymousQuotationAir = (ids: (string | number)[], access: LinkAccess) =>
  request.post<boolean>('/price/quotation/air/anonymous/batchDelete', { ids, token: access.token })
